import { AsseticTheme } from 'utils/theme';
import { useTheme } from '@emotion/react';
import { css } from '@emotion/css';
import { CSSProperties } from 'react';
import memoizeOne from 'memoize-one';
import { isFunction } from 'lodash';
import { ClassNameMap } from 'types/models';

export interface NestedCssProperties<Props> extends CSSProperties {
  // Allow string selector
  [k: string]: CSSProperties[keyof CSSProperties] | NestedCssProperties<Props>;
}
type PropsCSSProperties<Props> = (props?: Props) => NestedCssProperties<Props>;

export type StyledContent<props> =
  | CSSProperties
  | NestedCssProperties<props>
  | PropsCSSProperties<props>;

export interface ThemeToCssCb<
  TPartialProps,
  TStyleKey extends string = string,
> {
  (theme: AsseticTheme): Record<TStyleKey, StyledContent<TPartialProps>>;
}

export interface IUseStyleHook<
  TPartialProps,
  TStyleKey extends string = string,
> {
  (props?: TPartialProps): ClassNameMap<TStyleKey>;
}

const applyCss = <TProps, StyleKey extends string>(
  styles: Record<StyleKey, StyledContent<TProps>>,
  props?: TProps,
) => {
  const result = Object.entries(styles).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: css(isFunction(value) ? value(props) : value),
    }),
    {},
  );
  return result as Record<StyleKey, string>;
};

/**
 * Helper method that can be used in place of material-ui `makeStyles` to create `@emotion/css` style object
 * @param cb A function that return a style object in the format of { [key]: CSSProperties }
 * @returns A memoized function that can be used in a react component similar to material-ui `useStyles`
 * @example
 *    
      interface Props {
        visible: boolean;
      }

      const useStyles = makeThemeStyle((theme) => ({
        root: (props: Props) => ({
          backgroundColor: 'red',
          borderRadius: theme.spacing(1),
          padding: theme.spacing(1, 1),
          display: props.visible ? 'block' : 'none',
        }),
      }));

      const Component: React.FC = () => {
        const styles = useStyles({visible: true});
        return <span className={styles.root}>A Label </span>
      }
 */
export const makeThemeStyle = <
  TProps,
  StyleKey extends string = string,
  PartialProps = Partial<TProps>,
>(
  cb: ThemeToCssCb<PartialProps, StyleKey>,
): IUseStyleHook<PartialProps, StyleKey> => {
  //Memo the transformation fn so multiple calls to the returning function won't run style extraction
  const createStyles = memoizeOne(applyCss);

  return (props?: PartialProps) => {
    const theme = useTheme() as AsseticTheme;
    const styles = cb(theme);
    return createStyles(styles, props);
  };
};
